import { useMemo, useState } from 'react';

import { Button, SearchInput } from '@monksflow/monks-ui';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PaginationControls } from '@/shared/ui';
import { useTableData } from '@/shared/hooks/useTableData';
import { EmptyState } from '@/shared/ui';
import type { McpCatalogResponseDto } from '@inference.engine/api-client/web';

import { useDeleteCatalogMcp, useGetCatalogMcps } from '../../model/useCatalogMcp';
import { McpCatalogFormSheet } from './McpCatalogFormSheet';
import { McpCatalogTable } from './McpCatalogTable';
import type { McpCatalogRow } from './McpCatalogTable.types';

// `useTableData` leaves the direction undefined until the first sort, but a
// sortable head has to render one. Matches the sibling admin table.

/**
 * Knowledge Files paginates at ten, and `PaginationControls` renders nothing while
 * everything fits on one page — so a catalog of a handful of servers shows no control
 * at all, and a long one still stays navigable.
 */
const PAGE_SIZE = 10;

const toRow = (entry: McpCatalogResponseDto): McpCatalogRow => ({
  id: entry.id,
  name: entry.name,
  description: entry.description ?? '',
  url: entry.url,
  authType: entry.authType,
  createdAt: entry.createdAt as unknown as string,
});

interface SheetState {
  open: boolean;
  entry: McpCatalogResponseDto | null;
  /**
   * Bumped on every open so the sheet remounts and seeds itself from `entry`. Left
   * alone on close, which is what lets the exit animation play over the values the
   * form held rather than over defaults.
   */
  openCount: number;
}

/**
 * The MCP catalog itself: a toolbar row over a table, on whatever surface the page
 * frame supplies. It draws no card, border or inset of its own — the frame already
 * owns all three, and a second set of them is what made this read as a card inside
 * a table inside a table.
 */
export const McpCatalogPanel = () => {
  const { t } = useTranslation('admin');

  const { data: entries = [], isLoading } = useGetCatalogMcps();
  const deleteMutation = useDeleteCatalogMcp();

  const [sheet, setSheet] = useState<SheetState>({ open: false, entry: null, openCount: 0 });

  const rows = useMemo(() => entries.map(toRow), [entries]);
  const table = useTableData<McpCatalogRow>({
    data: rows,
    initialSort: { key: 'createdAt', direction: 'desc' },
    initialRowsPerPage: PAGE_SIZE,
    searchFields: ['name', 'url'],
  });

  const openSheet = (entry: McpCatalogResponseDto | null) => {
    setSheet((current) => ({ open: true, entry, openCount: current.openCount + 1 }));
  };
  const closeSheet = () => {
    setSheet((current) => ({ ...current, open: false }));
  };

  const addLabel = t('admin.mcpCatalogPanel.addMcpServer');
  const searchLabel = t('admin.mcpCatalogPanel.searchMcpByNameOrUrl');
  const isEmpty = !isLoading && entries.length === 0;
  const hasNoMatches = !isLoading && !isEmpty && table.totalCount === 0;

  return (
    <div data-testid="mcp-catalog-panel" className="flex min-h-0 flex-1 flex-col gap-md">
      {isEmpty ? (
        <EmptyState
          title={t('admin.mcpCatalogPanel.noMcpServersInThe')}
          description={t('admin.mcpCatalogPanel.addMcpServersThatMembers')}
        >
          <Button onClick={() => openSheet(null)}>
            <PlusIcon className="size-4" />
            {addLabel}
          </Button>
        </EmptyState>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2xl">
            <SearchInput
              value={table.searchValue}
              onValueChange={table.setSearchValue}
              className="w-80"
              placeholder={searchLabel}
              aria-label={searchLabel}
            />
            <Button onClick={() => openSheet(null)}>
              <PlusIcon className="size-4" />
              {addLabel}
            </Button>
          </div>

          {hasNoMatches ? (
            <EmptyState
              icon={<SearchIcon />}
              className="flex-1"
              title={t('admin.mcpCatalogPanel.noMatchesTitle')}
              description={t('admin.mcpCatalogPanel.noMatchesDescription')}
            />
          ) : (
            <>
              <McpCatalogTable
                rows={table.paginatedData}
                isLoading={isLoading}
                sortBy={table.sortBy}
                sortDirection={table.sortDirection}
                onSort={table.toggleSort}
                onEdit={(id) => openSheet(entries.find((entry) => entry.id === id) ?? null)}
                onDelete={(id) => deleteMutation.mutate({ id })}
                deleteDisabled={deleteMutation.isPending}
              />

              <PaginationControls
                page={table.page + 1}
                totalItems={table.totalCount}
                pageSize={table.rowsPerPage}
                onPageChange={(nextPage) => {
                  table.handlePageChange(nextPage - 1);
                }}
              />
            </>
          )}
        </>
      )}

      <McpCatalogFormSheet
        key={sheet.openCount}
        open={sheet.open}
        entry={sheet.entry}
        onClose={closeSheet}
      />
    </div>
  );
};
